import { useCallback, useState } from 'react'
import { BaseError, ContractFunctionRevertedError } from 'viem'
import { usePublicClient } from 'wagmi'
import { getWalletClient } from 'wagmi/actions'
import { liveGasParams } from 'utils/liveGas'
import { useV3Wallet } from 'views/TradingCompetitionV3/hooks'

/** Every on-chain profile action runs on BNB Smart Chain (the CADINU profile, referral and NFT contracts live there only). */
export const PROFILE_CHAIN_ID = 56

export type TxState = 'idle' | 'wallet' | 'pending' | 'success' | 'error'

/** What the contracts answer, in words (custom errors of CadinuProfile / CadinuReferral / the NFT and CBON contracts). */
const REASONS: [RegExp, string][] = [
  [/__errorUserNotActive/, 'Your on-chain profile is paused. Reactivate it first.'],
  [/__errorUserIsActive/, 'Your on-chain profile is already active.'],
  [/__errorUserAlreadyRegistered/, 'This wallet already has an on-chain profile.'],
  [/__errorUserNotRegistered/, 'This wallet has no on-chain profile yet.'],
  [/__errorNotNftOwner/, 'This wallet does not own that NFT.'],
  [/__errorNotCadinuLevelNft/, 'Only CADINU Identity Arts NFTs can be locked.'],
  [/__errorWrongReferral/, 'You cannot refer yourself.'],
  [/__errorNoTokenToWithdraw/, 'Nothing to withdraw yet.'],
  [/caller is not token owner or approved|not approved/i, 'The NFT is not approved for the profile contract yet. Approve it first.'],
  [/TransferAmountExceedsAllowance|insufficient allowance|0x0d90bf60/i, 'CBON is not approved for this amount yet. Approve it first.'],
  [/TransferAmountExceedsBalance|exceeds balance/i, 'Not enough CBON in this wallet.'],
  [/insufficient funds/i, 'Not enough BNB for the network fee.'],
]

export function explainTxError(e: unknown): string {
  const any = e as { name?: string; shortMessage?: string; message?: string; details?: string; code?: number }
  const all = [any?.name, any?.shortMessage, any?.details, any?.message].filter(Boolean).join(' ')
  if (any?.code === 4001 || /user rejected|reject|denied|cancel/i.test(all)) return 'Cancelled in the wallet.'
  if (e instanceof BaseError) {
    const revert = e.walk((x) => x instanceof ContractFunctionRevertedError) as ContractFunctionRevertedError | null
    const name = revert?.data?.errorName ?? revert?.reason ?? ''
    const hit = REASONS.find(([re]) => re.test(name) || re.test(all))
    if (hit) return hit[1]
    if (revert) return `The contract refused it${name ? ` (${name})` : ''}.`
  }
  const hit = REASONS.find(([re]) => re.test(all))
  return hit ? hit[1] : String(any?.shortMessage || any?.message || e).split('\n')[0].slice(0, 180)
}

/**
 * One contract write from the connected wallet with visible states: `wallet` while the wallet asks for confirmation,
 * `pending` while the transaction is mined, then `success` or `error` (with the reason in words). The call is simulated
 * first (eth_call from the wallet) so a call the contract would refuse never reaches the wallet; a wallet on another chain
 * is asked to switch to BNB Smart Chain first. Gas price = the chain's live price (utils/liveGas), never a pinned number.
 */
export function useChainTx() {
  const publicClient = usePublicClient({ chainId: PROFILE_CHAIN_ID })
  const { address, isConnected, wrongChain, switchToBsc } = useV3Wallet()
  const [state, setState] = useState<TxState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [hash, setHash] = useState<string | null>(null)
  const reset = useCallback(() => {
    setState('idle')
    setError(null)
    setHash(null)
  }, [])
  const run = useCallback(
    async (req: { address: `0x${string}`; abi: any; functionName: string; args?: readonly unknown[] }) => {
      setError(null)
      setHash(null)
      if (!isConnected || !address) {
        setState('error')
        setError('Connect a wallet first.')
        return false
      }
      setState('wallet')
      try {
        if (wrongChain && !(await switchToBsc())) throw new Error('Switch your wallet to BNB Smart Chain to continue.')
        try {
          await publicClient.simulateContract({ ...req, account: address } as any)
        } catch (sim) {
          // a refusal by the contract stops here; a node that cannot answer leaves the decision to the wallet
          if (sim instanceof BaseError && sim.walk((x) => x instanceof ContractFunctionRevertedError)) throw sim
        }
        const wallet = await getWalletClient({ chainId: PROFILE_CHAIN_ID })
        if (!wallet) throw new Error('Wallet is not ready on BNB Smart Chain.')
        const h = await wallet.writeContract({ ...(req as any), ...(await liveGasParams(PROFILE_CHAIN_ID)), chain: wallet.chain, account: wallet.account })
        setHash(h)
        setState('pending')
        const receipt = await publicClient.waitForTransactionReceipt({ hash: h })
        if (receipt.status !== 'success') throw new Error('The transaction reverted on chain.')
        setState('success')
        return h as string
      } catch (e) {
        setError(explainTxError(e))
        setState('error')
        return false
      }
    },
    [address, isConnected, wrongChain, switchToBsc, publicClient],
  )
  return { state, error, hash, run, reset, busy: state === 'wallet' || state === 'pending' }
}

export const txLabel = (state: TxState, idle: string, done = 'Done') =>
  state === 'wallet' ? 'Confirm in your wallet…' : state === 'pending' ? 'Waiting for BNB Chain…' : state === 'success' ? done : idle
